import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Clock, ChevronDown, ChevronUp, X, Menu, MessageCircle, ArrowUp, Eye } from "lucide-react";
import { LOGO_HORIZONTAL, CONTACT, SERVICES } from "@/data/constants";
import { useAppointment } from "@/components/AppointmentModal";
import { PhoneLink } from "@/components/PhoneLink";

// Order of services in the top navigation dropdown (must match user spec)
const NAV_SERVICES_ORDER = ["katarakta", "diagnostyka", "presbyopia", "sitkivka", "kids"];
const NAV_SERVICE_TITLES = {
  katarakta: "Лікування катаракти та глаукоми",
  diagnostyka: "Діагностика зору",
  presbyopia: "Лікування пресбіопії",
  sitkivka: "Лікування захворювань сітківки",
  kids: "Дитяча офтальмологія",
};

const NAV_LINKS = [
  {
    label: "Послуги", href: "#",
    children: NAV_SERVICES_ORDER.map((slug) => ({ label: NAV_SERVICE_TITLES[slug], href: `/${slug}` })),
  },
  { label: "Тест", href: "/test" },
  { label: "Лікарі", href: "/#doctors-section" },
  { label: "Відгуки", href: "/#reviews-section" },
  { label: "Блог", href: "/blog" },
  { label: "Новини", href: "/news" },
  { label: "Контакти", href: "/#contacts" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { open: openAppointment } = useAppointment();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  return (
    <header className="fixed top-0 w-full z-50 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]" data-testid="main-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img
              src={LOGO_HORIZONTAL}
              alt="Центр мікрохірургії ока «Ваш зір»"
              className="h-[72px] lg:h-[96px] transition-all duration-300"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-12" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <div key={link.label} className="relative group">
                {link.children ? (
                  <Link
                    to="/#services-section"
                    className="nav-link text-base font-medium text-[#333] hover:text-[#E8590A] transition-colors"
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                    data-testid={`nav-${link.label}`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    to={link.href}
                    className="nav-link text-base font-medium text-[#333] hover:text-[#E8590A] transition-colors"
                    data-testid={`nav-${link.label}`}
                  >
                    {link.label}
                  </Link>
                )}
                {link.children && (
                  <div
                    className="absolute top-full left-0 pt-2"
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <AnimatePresence>
                      {openDropdown === link.label && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.2 }}
                          className="bg-white rounded-xl shadow-lg border border-[#F0F0F0] py-2 min-w-[260px]"
                        >
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-5 py-2.5 text-sm text-[#333] hover:bg-[#F5F5F5] hover:text-[#E8590A] transition-colors"
                              data-testid={`nav-dropdown-${child.label}`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <div className="flex flex-col items-end leading-tight">
              <PhoneLink href={CONTACT.phone1Link} className="text-[15px] font-semibold text-[#333] hover:text-[#E8590A] transition-colors">
                {CONTACT.phone1}
              </PhoneLink>
              <PhoneLink href={CONTACT.phone2Link} className="text-[15px] font-semibold text-[#333] hover:text-[#E8590A] transition-colors">
                {CONTACT.phone2}
              </PhoneLink>
            </div>
            <button
              onClick={openAppointment}
              className="bg-[#E8590A] text-white px-7 py-3 rounded-full hover:bg-[#D14D08] transition-colors duration-300 text-base font-semibold"
              data-testid="header-appointment-btn"
            >
              Записатись
            </button>
          </div>

          {/* Mobile burger + quick phone */}
          <div className="lg:hidden flex items-center gap-2">
            <PhoneLink
              href={CONTACT.phone1Link}
              className="inline-flex items-center gap-1.5 bg-[#E8590A] text-white px-3.5 py-2 rounded-full text-[13px] font-semibold shadow-[0_4px_14px_-4px_rgba(232,89,10,0.45)] active:scale-95 transition-transform"
              data-testid="mobile-header-phone"
              aria-label={`Зателефонувати: ${CONTACT.phone1}`}
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={2.5} />
              {CONTACT.phone1}
            </PhoneLink>
            <button
              className="p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <span className={`burger-line block h-0.5 w-6 rounded bg-[#1C1C1E] ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
                <span className={`burger-line block h-0.5 w-6 rounded bg-[#1C1C1E] ${mobileOpen ? "opacity-0" : ""}`} />
                <span className={`burger-line block h-0.5 w-6 rounded bg-[#1C1C1E] ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 top-24 bg-white z-40 lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-8 space-y-1">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {link.children ? (
                    <div>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                        className="w-full flex items-center justify-between py-3 text-lg font-sans font-medium text-[#1C1C1E]"
                      >
                        {link.label}
                        {openDropdown === link.label ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      <AnimatePresence>
                        {openDropdown === link.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden pl-4"
                          >
                            {link.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                className="block py-2.5 text-base text-[#808080] hover:text-[#E8590A]"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link to={link.href} className="block py-3 text-lg font-medium text-[#333] hover:text-[#E8590A]">
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}
              <div className="pt-6 border-t border-[#F0F0F0] mt-4 space-y-4">
                <PhoneLink href={CONTACT.phone1Link} className="flex items-center gap-3 text-[#333]">
                  <Phone className="w-5 h-5 text-[#E8590A]" /> {CONTACT.phone1}
                </PhoneLink>
                <PhoneLink href={CONTACT.phone2Link} className="flex items-center gap-3 text-[#333]">
                  <Phone className="w-5 h-5 text-[#E8590A]" /> {CONTACT.phone2}
                </PhoneLink>
                <PhoneLink href={CONTACT.phone3Link} className="flex items-center gap-3 text-[#333]">
                  <Phone className="w-5 h-5 text-[#E8590A]" /> {CONTACT.phone3}
                </PhoneLink>
                <button
                  onClick={() => { openAppointment(); setMobileOpen(false); }}
                  className="block w-full text-center bg-[#E8590A] text-white px-6 py-3 rounded-full hover:bg-[#D14D08] transition-colors font-semibold"
                  data-testid="mobile-appointment-btn"
                >
                  Записатись на консультацію
                </button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#1C1C1E] text-white pt-16 pb-8" data-testid="main-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        <div>
          <Link to="/" className="inline-block mb-5" data-testid="footer-logo">
            <img
              src={LOGO_HORIZONTAL}
              alt="Центр мікрохірургії ока «Ваш зір»"
              className="h-14"
              style={{ filter: "invert(1) hue-rotate(180deg)" }}
            />
          </Link>
          <p className="font-sans text-sm text-white/60 leading-relaxed">
            Центр сучасної офтальмології з понад 30-річною історією. Проводимо повний комплекс діагностики та лікування захворювань ока на експертному рівні.
          </p>
        </div>
        <div>
          <h4 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#E8590A] mb-4">Послуги</h4>
          <ul className="space-y-2">
            {NAV_SERVICES_ORDER.map((slug) => (
              <li key={slug}>
                <Link to={`/${slug}`} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{NAV_SERVICE_TITLES[slug]}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#E8590A] mb-4">Навігація</h4>
          <ul className="space-y-2">
            <li><Link to="/test" className="font-sans text-sm text-white/60 hover:text-white transition-colors">Тест зору</Link></li>
            <li><Link to="/news" className="font-sans text-sm text-white/60 hover:text-white transition-colors">Новини</Link></li>
            <li><Link to="/#doctors-section" className="font-sans text-sm text-white/60 hover:text-white transition-colors">Лікарі</Link></li>
            <li><Link to="/#reviews-section" className="font-sans text-sm text-white/60 hover:text-white transition-colors">Відгуки</Link></li>
            <li><Link to="/#contacts" className="font-sans text-sm text-white/60 hover:text-white transition-colors">Контакти</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#E8590A] mb-4">Контакти</h4>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#E8590A] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <span className="font-sans text-sm text-white/60">{CONTACT.address}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#E8590A] flex-shrink-0" strokeWidth={1.75} />
              <PhoneLink href={CONTACT.phone1Link} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{CONTACT.phone1}</PhoneLink>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#E8590A] flex-shrink-0" strokeWidth={1.75} />
              <PhoneLink href={CONTACT.phone2Link} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{CONTACT.phone2}</PhoneLink>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#E8590A] flex-shrink-0" strokeWidth={1.75} />
              <PhoneLink href={CONTACT.phone3Link} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{CONTACT.phone3}</PhoneLink>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-[#E8590A] flex-shrink-0" strokeWidth={1.75} />
              <span className="font-sans text-sm text-white/60">{CONTACT.workHours}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="font-sans text-xs text-white/40">&copy; 2026 Центр мікрохірургії ока «Ваш зір». Всі права захищено.</p>
        <div className="flex items-center gap-4">
          <a href={CONTACT.viber} className="text-white/40 hover:text-[#E8590A] transition-colors" aria-label="Viber">
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
          </a>
          <a href={CONTACT.telegram} className="text-white/40 hover:text-[#E8590A] transition-colors" aria-label="Telegram">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
          </a>
          <a href={CONTACT.whatsapp} className="text-white/40 hover:text-[#E8590A] transition-colors" aria-label="WhatsApp">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// Floating elements
const FloatingElements = () => {
  const [showTop, setShowTop] = useState(false);
  const { open: openAppointment } = useAppointment();

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Back to top */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-44 right-6 z-40 w-12 h-12 bg-white rounded-full shadow-lg border border-[#F0F0F0] flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
            data-testid="back-to-top-btn"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5 text-[#E8590A]" strokeWidth={1.75} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating phone / appointment button */}
      <button
        onClick={openAppointment}
        className="fixed bottom-28 right-6 z-40 w-14 h-14 rounded-full bg-[#E8590A] shadow-[0_10px_30px_-8px_rgba(232,89,10,0.6)] flex items-center justify-center hover:bg-[#D14D08] hover:scale-105 transition-all animate-pulse-ring"
        data-testid="floating-phone-btn"
        aria-label="Call us"
      >
        <span className="absolute inset-0 rounded-full bg-[#E8590A] opacity-60 animate-ping-slow" />
        <Phone className="w-6 h-6 text-white relative z-10" strokeWidth={2.25} />
      </button>
    </>
  );
};

// Sticky Stats Ticker
const StickyTicker = () => {
  const items = [
    { before: "понад ", number: "250 000", after: " обстежень" },
    { before: "більше ", number: "60 000", after: " операцій" },
    { before: "", number: "30", after: " років досвіду" },
    { before: "понад ", number: "250 000", after: " обстежень" },
    { before: "більше ", number: "60 000", after: " операцій" },
    { before: "", number: "30", after: " років досвіду" },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 overflow-hidden bg-[#E8590A]" data-testid="sticky-ticker">
      <div
        className="flex whitespace-nowrap py-5"
        style={{
          animation: "ticker-scroll 25s linear infinite",
          width: "fit-content",
        }}
      >
        {[...items, ...items].map((item, i) => (
          <div key={i} className="flex items-baseline mx-12 flex-shrink-0">
            <span className="text-base text-white/80">{item.before}</span>
            <span className="text-3xl sm:text-4xl font-bold text-white mx-1.5">{item.number}</span>
            <span className="text-base text-white/80">{item.after}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Smooth scroll to anchor (with retry to wait for content to mount)
      const id = location.hash.slice(1);
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 10) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pb-24 lg:pb-28">{children}</main>
      <Footer />
      <FloatingElements />
      <StickyTicker />
    </div>
  );
};
